import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import type { Tournament } from '@shared/schema';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, Users, Trophy, UserPlus, Eye } from 'lucide-react';
import { TournamentLogo } from '@/components/tournament/tournament-logo';
import { TournamentDetailModal } from '@/components/tournament/tournament-detail-modal';
import { formatCalendarDateRange, isTournamentLive, isTournamentUpcoming } from '@shared/timeUtils';
import { useDebounce } from '@/lib/debounce';
import { useLandingSearch } from '@/components/landing/landing-search-context';

type DirectoryTournament = Tournament & {
  organization?: { name?: string | null } | null;
};

export function TournamentDirectoryBroadcast() {
  const landingSearch = useLandingSearch();
  const searchQuery = landingSearch?.searchQuery ?? '';
  const eventFilter = landingSearch?.eventFilter ?? 'all';
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: allTournaments,
    isLoading: tournamentsLoading,
    isError: tournamentsFailed,
  } = useQuery<DirectoryTournament[]>({
    queryKey: ['/api/public/tournaments'],
    retry: false,
  });

  const publicTournaments = useMemo(() => {
    if (!allTournaments) return [];
    return allTournaments.filter(t => t && t.visibility === 'public');
  }, [allTournaments]);

  const { activeTournaments, upcomingTournaments } = useMemo(() => {
    if (!publicTournaments) return { activeTournaments: [], upcomingTournaments: [] };

    const filtered = publicTournaments.filter(tournament => {
      const needle = debouncedSearchQuery.toLowerCase().trim();
      const orgName = tournament.organization?.name ?? '';
      const haystack = `${tournament.customName || ''} ${tournament.name} ${orgName}`.toLowerCase();
      const matchesSearch = needle ? haystack.includes(needle) : true;
      const matchesType = typeFilter === 'all' || tournament.type === typeFilter;
      return matchesSearch && matchesType;
    });

    const active: Tournament[] = [];
    const upcoming: Tournament[] = [];

    filtered.forEach(tournament => {
      if (isTournamentLive(tournament.startDate ?? '', tournament.endDate ?? '')) {
        active.push(tournament);
      } else if (isTournamentUpcoming(tournament.startDate ?? '')) {
        upcoming.push(tournament);
      }
    });

    active.sort(
      (a, b) => new Date(a.startDate ?? '').getTime() - new Date(b.startDate ?? '').getTime()
    );
    upcoming.sort(
      (a, b) => new Date(a.startDate ?? '').getTime() - new Date(b.startDate ?? '').getTime()
    );

    return { activeTournaments: active, upcomingTournaments: upcoming };
  }, [publicTournaments, debouncedSearchQuery, typeFilter]);

  const visibleActive = eventFilter === 'upcoming' ? [] : activeTournaments;
  const visibleUpcoming = eventFilter === 'active' ? [] : upcomingTournaments;
  const filtersActive =
    Boolean(searchQuery.trim()) || typeFilter !== 'all' || eventFilter !== 'all';

  return (
    <section
      id="tournaments-directory"
      className="py-16 md:py-24 bg-sport-black"
      data-testid="tournament-directory"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-broadcast text-3xl md:text-5xl text-white mb-4 tracking-wide uppercase">
              FIND YOUR TOURNAMENT
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Find active tournaments happening now or register for upcoming events
            </p>
          </div>

          {tournamentsFailed && (
            <div className="mb-4 p-4 rounded-lg border border-white/15 bg-white/5 text-white/80 text-sm">
              The event list is still loading. Check back shortly, or try the search again.
            </div>
          )}

          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-white/60">
              {searchQuery.trim()
                ? `Showing matches for “${searchQuery.trim()}”`
                : 'Search from the top of the page, or browse what is on now.'}
            </p>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger
                className="w-full md:w-48 h-12 bg-white/5 border-white/10 text-white min-h-[48px]"
                aria-label="Filter tournaments by type"
              >
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-pro-gunmetal border-white/10">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="pool_play">Pool Play</SelectItem>
                <SelectItem value="single_elimination">Single Elimination</SelectItem>
                <SelectItem value="double_elimination">Double Elimination</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {tournamentsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Skeleton className="h-12 w-12 rounded-lg bg-white/10" />
                    <Skeleton className="h-6 flex-1 bg-white/10" />
                  </div>
                  <Skeleton className="h-4 w-32 bg-white/10 mb-4" />
                  <Skeleton className="h-10 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {visibleActive.length > 0 && (
                <div className="mb-12">
                  <h3 className="font-broadcast text-2xl md:text-3xl text-white mb-6 tracking-wide uppercase">
                    ACTIVE TOURNAMENTS
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleActive.map(tournament => (
                      <div
                        key={tournament.id}
                        className="bg-white/5 backdrop-blur-md border-l-4 border-green-500 border border-white/10 rounded-xl p-6 transition-all hover:border-sport-yellow/20 hover:bg-white/[0.07]"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                          <TournamentLogo tournament={tournament} size="sm" />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-broadcast text-lg text-white line-clamp-2">
                              {tournament.customName || tournament.name}
                            </h4>
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-400 mt-1">
                              <Trophy className="w-3 h-3" />
                              Live Now
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-white/60 mb-2">
                          <CalendarDays className="w-4 h-4" />
                          <span>
                            {formatCalendarDateRange(tournament.startDate, tournament.endDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-white/50 mb-4">
                          <Users className="w-4 h-4" />
                          <span>{tournament.numberOfTeams || 0} Teams</span>
                        </div>
                        <Button
                          asChild
                          className="w-full bg-pro-field text-white hover:bg-pro-field/90 font-broadcast min-h-[48px] touch-manipulation"
                        >
                          <Link href={`/tournament/${tournament.id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Tournament
                          </Link>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {visibleUpcoming.length > 0 && (
                <div className={visibleActive.length > 0 ? 'mt-12' : ''}>
                  <h3 className="font-broadcast text-2xl md:text-3xl text-white mb-6 tracking-wide uppercase">
                    UPCOMING TOURNAMENTS
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleUpcoming.map(tournament => {
                      const isRegistrationOpen = tournament.isRegistrationOpen === true;
                      return (
                        <div
                          key={tournament.id}
                          className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 transition-all hover:border-sport-yellow/20 hover:bg-white/[0.07]"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <TournamentLogo tournament={tournament} size="sm" />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-broadcast text-lg text-white line-clamp-2">
                                {tournament.customName || tournament.name}
                              </h4>
                              {isRegistrationOpen && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-championship-gold mt-1 animate-pulse">
                                  <UserPlus className="w-3 h-3" />
                                  Registration Open
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-white/60 mb-2">
                            <CalendarDays className="w-4 h-4" />
                            <span>
                              {formatCalendarDateRange(tournament.startDate, tournament.endDate)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-white/50 mb-4">
                            <Users className="w-4 h-4" />
                            <span>{tournament.numberOfTeams || 0} Teams</span>
                          </div>
                          <div className="flex flex-col gap-2">
                            {isRegistrationOpen && (
                              <Button
                                asChild
                                className="w-full bg-pro-clay text-white hover:bg-pro-clay/90 font-broadcast min-h-[48px] touch-manipulation"
                              >
                                <Link href={`/tournaments/${tournament.id}/register`}>
                                  <UserPlus className="w-4 h-4 mr-2" />
                                  Register Team
                                </Link>
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              className="w-full border-white/30 text-white hover:bg-white/10 font-broadcast min-h-[48px]"
                              onClick={() => {
                                setSelectedTournament(tournament);
                                setIsModalOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {visibleActive.length === 0 && visibleUpcoming.length === 0 && (
                <div className="text-center py-16">
                  <Trophy className="w-16 h-16 text-white/40 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {filtersActive ? 'No tournaments found' : 'No tournaments available'}
                  </h3>
                  <p className="text-white/60">
                    {filtersActive
                      ? 'Try another name, organization, or filter.'
                      : 'Check back soon for upcoming tournaments'}
                  </p>
                  {filtersActive && (
                    <Button
                      variant="link"
                      onClick={() => {
                        landingSearch?.setSearchQuery('');
                        landingSearch?.setEventFilter('all');
                        setTypeFilter('all');
                      }}
                      className="mt-4 text-sport-yellow"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedTournament && (
        <TournamentDetailModal
          tournament={selectedTournament}
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </section>
  );
}
